import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Blocks,
  Monitor,
  Code,
  Gamepad2,
  Terminal,
  Bot,
  ChevronRight,
  Clock,
  Users,
  CheckCircle,
  Sparkles,
} from "lucide-react";

const programs = [
  {
    name: "Foundations of Logic & Coding",
    ages: "Ages 6–7",
    step: "Stage 1",
    duration: "12 weeks",
    groupSize: "6–8 students",
    desc: "Your child's first step into the world of programming. Through visual blocks, puzzles, and interactive games, kids develop logical thinking and learn the core concepts that make all coding possible.",
    outcomes: [
      "Develop logical and sequential thinking",
      "Understand basic programming concepts",
      "Complete interactive puzzles and challenges",
      "Build confidence with technology from an early age",
    ],
    icon: Blocks,
    color: "#1677FF",
    bgColor: "#EAF2FF",
    image: "https://customer-assets.emergentagent.com/job_008705cc-4791-46f2-aff2-0741ecdfeee7/artifacts/7kcbuwz9_Foundations%20of%20Logic%20%26%20Coding.png",
  },
  {
    name: "Computer & Digital Skills",
    ages: "Ages 7–9",
    step: "Stage 2",
    duration: "12 weeks",
    groupSize: "6–8 students",
    desc: "Children explore how computers work, learn digital safety fundamentals, and create their first simple digital projects — building a solid foundation for all future tech learning.",
    outcomes: [
      "Understand how computers and the internet work",
      "Learn digital safety and responsible online behavior",
      "Create first digital projects independently",
      "Develop typing and computer navigation skills",
    ],
    icon: Monitor,
    color: "#F28C38",
    bgColor: "#FFF1E6",
    image: "https://customer-assets.emergentagent.com/job_008705cc-4791-46f2-aff2-0741ecdfeee7/artifacts/zyb3ygtb_Computer%20%26%20Digital%20Skills.png",
  },
  {
    name: "Visual Programming with Scratch",
    ages: "Ages 8–10",
    step: "Stage 3",
    duration: "16 weeks",
    groupSize: "6–8 students",
    desc: "Students bring their ideas to life by building animations and their own games in Scratch. They master programming logic, loops, conditionals, and algorithms — all through creative, project-based learning experiences.",
    outcomes: [
      "Build complete animations and interactive games",
      "Master loops, conditionals, and variables",
      "Learn algorithmic thinking and debugging",
      "Present and share finished projects with peers",
    ],
    icon: Code,
    color: "#00A676",
    bgColor: "#E7FFF6",
    image: "https://customer-assets.emergentagent.com/job_008705cc-4791-46f2-aff2-0741ecdfeee7/artifacts/khfrnpq1_Visual%20Programming.png",
  },
  {
    name: "Game Design & Development",
    ages: "Ages 10–12",
    step: "Stage 4",
    duration: "16 weeks",
    groupSize: "6–8 students",
    desc: "Kids design complete games with original characters, mechanics, and levels. They learn storytelling, level design, and creative problem-solving — turning their imagination into playable projects.",
    outcomes: [
      "Design and build complete, playable games",
      "Create original characters, stories, and levels",
      "Learn game mechanics and user experience basics",
      "Develop creative thinking and project planning skills",
    ],
    icon: Gamepad2,
    color: "#9333EA",
    bgColor: "#F3E8FF",
    image: "https://customer-assets.emergentagent.com/job_008705cc-4791-46f2-aff2-0741ecdfeee7/artifacts/d6ip7gdn_Game%20Design%20%26%20Development.png",
  },
  {
    name: "Python Programming",
    ages: "Ages 11–14",
    step: "Stage 5",
    duration: "16 weeks",
    groupSize: "6–8 students",
    desc: "Students transition to real-world coding with Python — one of the most widely used programming languages. They build practical projects, mini-games, and their first applications while learning industry-standard skills.",
    outcomes: [
      "Write real Python programs from scratch",
      "Build mini-games, tools, and simple applications",
      "Master variables, functions, loops, and structures",
      "Prepare for advanced programming and CS",
    ],
    icon: Terminal,
    color: "#D52B1E",
    bgColor: "#FFE9E7",
    image: "https://customer-assets.emergentagent.com/job_008705cc-4791-46f2-aff2-0741ecdfeee7/artifacts/han6yudj_Visual%20Programming.png",
  },
  {
    name: "Robotics & STEM Projects",
    ages: "Ages 12–15",
    step: "Stage 6",
    duration: "16 weeks",
    groupSize: "6–8 students",
    desc: "The most advanced stage combines coding with hands-on engineering. Kids build and program robots while applying engineering principles, physics basics, and advanced problem-solving in collaborative team projects.",
    outcomes: [
      "Build and program functional robots",
      "Understand sensors, motors, and circuits",
      "Apply engineering and physics concepts practically",
      "Solve complex STEM challenges in teams",
    ],
    icon: Bot,
    color: "#0891B2",
    bgColor: "#E0F7FA",
    image: "https://customer-assets.emergentagent.com/job_008705cc-4791-46f2-aff2-0741ecdfeee7/artifacts/uv6k2xf6_Robotics%20%26%20STEM%20Projects.png",
  },
];

export default function Programs() {
  return (
    <div data-testid="programs-page" className="pt-20">
      {/* Header */}
      <section className="pt-[35px] pb-[35px] md:py-20 playzone-gradient noise-bg">
        <div className="mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold bg-[#FFF1E6] text-[#F28C38] mb-4">
              <Sparkles className="w-3 h-3" /> Structured Learning
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
              Our Learning{" "}
              <span className="text-[#1677FF]">Path</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-[85%] md:max-w-2xl mx-auto">
              A step-by-step educational journey from ages 6 to 15. Each stage builds on the previous one, developing your child's logic, creativity, and real digital skills.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Programs List - Alternating Layouts */}
      <section className="pt-[34px] pb-16 md:pt-[26px] md:pb-24">
        <div className="mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6">
          <div className="space-y-20">
            {programs.map((program, i) => (
              <motion.div
                key={program.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6 }}
                data-testid={`program-detail-${i}`}
                className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                  i % 2 === 1 ? "lg:direction-rtl" : ""
                }`}
              >
                {/* Image */}
                <div className={`relative ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div
                    className="absolute -inset-3 rounded-[2rem] -rotate-1"
                    style={{ backgroundColor: program.bgColor }}
                  ></div>
                  <img
                    src={program.image}
                    alt={program.name}
                    className="relative rounded-2xl w-full h-[280px] md:h-[320px] object-cover shadow-lg"
                    loading="lazy"
                  />
                  {/* Floating badge */}
                  <div className="absolute -top-3 -right-3 bg-white rounded-xl px-3 py-2 shadow-lg border border-border flex items-center gap-2">
                    <program.icon className="w-5 h-5" style={{ color: program.color }} />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{program.step}</span>
                  </div>
                </div>

                {/* Content */}
                <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="flex items-center gap-3 mb-4">
                    <Badge variant="secondary" className="text-xs font-semibold rounded-full">{program.ages}</Badge>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" /> {program.duration}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Users className="w-3 h-3" /> {program.groupSize}
                    </span>
                  </div>

                  <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4">{program.name}</h2>
                  <p className="text-base text-muted-foreground leading-relaxed mb-6 lg:max-w-[95%]">{program.desc}</p>

                  <div className="space-y-2 mb-6">
                    {program.outcomes.map((o) => (
                      <div key={o} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: program.color }} />
                        <span className="text-sm">{o}</span>
                      </div>
                    ))}
                  </div>

                  <Link to="/contact">
                    <Button
                      className="rounded-full text-white btn-lift px-6"
                      style={{ backgroundColor: program.color }}
                    >
                      Book a Free Trial
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pt-[35px] pb-[35px] md:py-20 bg-[#1677FF] relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full bg-white/5"></div>
          <div className="absolute -bottom-16 -right-16 w-80 h-80 rounded-full bg-white/5"></div>
        </div>
        <div className="relative mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Not Sure Which Stage Is Right?
          </h2>
          <p className="text-base text-white/80 mb-8 max-w-md mx-auto">
            Book a free trial class and our instructors will help find the perfect starting point for your child.
          </p>
          <Link to="/contact">
            <Button size="lg" className="rounded-full bg-white text-[#1677FF] hover:bg-white/90 btn-lift px-8 h-12 text-base font-bold shadow-xl">
              Get Started
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
